import { useEffect } from 'react';
import { useToastStore, type Toast } from '@/stores/toastStore';

const MAX_VISIBLE = 3;

/**
 * Affiche jusqu'à 3 toasts empilés en bas à droite.
 * Auto-dismiss à expiresAt (setTimeout).
 */
export default function Toaster() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  // Auto-dismiss via setTimeout par toast
  useEffect(() => {
    if (toasts.length === 0) return;
    const timers = toasts.map((t) => {
      const delay = Math.max(0, t.expiresAt - Date.now());
      return window.setTimeout(() => dismiss(t.id), delay);
    });
    return () => {
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [toasts, dismiss]);

  if (toasts.length === 0) return null;

  // Affiche les N derniers toasts (les plus récents en bas, comme une pile)
  const visible = toasts.slice(-MAX_VISIBLE);

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-[360px]"
      role="region"
      aria-label="Notifications"
    >
      {visible.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const borderColor =
    toast.type === 'success'
      ? 'border-l-status-termine-text'
      : toast.type === 'error'
        ? 'border-l-status-annule-text'
        : 'border-l-ktm-orange';

  return (
    <div
      role="status"
      aria-live="polite"
      className={`bg-text-primary text-text-on-orange rounded-md shadow-lg px-4 py-3 text-sm flex items-start gap-3 border-l-4 ${borderColor}`}
    >
      <div className="flex-1 leading-snug break-words">{toast.message}</div>
      {toast.onUndo && toast.undoLabel && (
        <button
          type="button"
          onClick={() => {
            toast.onUndo?.();
            onDismiss();
          }}
          className="text-ktm-orange hover:underline text-xs font-semibold uppercase tracking-wider shrink-0"
        >
          {toast.undoLabel}
        </button>
      )}
      <button
        type="button"
        onClick={onDismiss}
        className="text-text-on-orange/60 hover:text-text-on-orange text-base leading-none shrink-0"
        aria-label="Fermer la notification"
      >
        ×
      </button>
    </div>
  );
}
